import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import React, { useRef, useState } from 'react';
import AutoComplete from '../../components/AutoComplete';
import SearchTextField, { ISearchTextField, ISearchTextFieldImperativeHandle } from '../../components/SearchTextField';
import { usePenname } from '../../hooks/queries/usePenname';
import useSnackbar from '../../hooks/useSnackbar';
import { IPenname, ISuggestion, ISuggestionSource } from '../../services/types';

interface IProps {
  sourcePennameId?: number;
  open: boolean;
  onMerge: (targetPenname: IPenname) => void;
  onClose: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  placeholder: { fontSize: '12px', fontWeight: 500, color: theme.palette.text.primary, opacity: 0.54, letterSpacing: 0, marginBottom: '20px' },
}));

const MergeDialog = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { sourcePennameId, open = false, onClose, onMerge } = props;

  const [targetPennameId, setTargetPennameId] = useState<number>();
  const searchTextFieldRef = useRef<ISearchTextFieldImperativeHandle>();

  const snackbar = useSnackbar();

  const { data: targetPenname, isLoading } = usePenname(targetPennameId);

  const handleCloseClick = () => onClose();

  const handleMergeClick = () => {
    if (targetPenname && verify()) {
      onMerge(targetPenname);
    }
  };

  const verify = (): boolean => {
    const selectedPenname = searchTextFieldRef.current?.getValue();
    const loadedPenname = targetPenname?.name;

    if (!selectedPenname || !loadedPenname) {
      snackbar.warning('No selected pen name.');
      return false;
    }

    if (selectedPenname.toLowerCase() !== loadedPenname.toLowerCase()) {
      snackbar.warning('The selected pen name and the pen name information stored on the server are different. Please contact the product team.');
      return false;
    }

    return true;
  };

  const handleEntered = () => searchTextFieldRef.current?.focus();

  return (
    <Dialog
      onClose={handleCloseClick}
      onEntered={handleEntered}
      open={open}
      PaperProps={{ style: { padding: '14px 6px', width: '470px', overflowY: 'visible' } }}
    >
      <DialogTitle disableTypography style={{ fontSize: '16px', fontWeight: 'bold', paddingBottom: '4px', letterSpacing: 0 }}>
        Would you like to merge this Pen Name to another?
      </DialogTitle>
      <DialogContent style={{ overflowY: 'visible' }}>
        <Typography className={classes.placeholder} gutterBottom>
          Once merged, this Pen Name will be deleted and the editing right will be moved to the other Pen Name.
        </Typography>
        <AutoComplete<ISearchTextField>
          type="penname"
          allowDuplicate={true}
          render={(props: ISearchTextField) => {
            const { value, onCancel, ...restProps } = props;

            return (
              <SearchTextField
                defaultValue={value as string}
                ref={searchTextFieldRef}
                onCancel={() => {
                  setTargetPennameId(undefined);
                  onCancel?.();
                }}
                onValueChange={() => {
                  setTargetPennameId(undefined);
                }}
                label="Merge to another Pen Name"
                InputLabelProps={{ shrink: true }}
                placeholder="Search"
                isSearching={isLoading}
                fullWidth
                {...restProps}
              />
            );
          }}
          filter={(suggestion: ISuggestion) => Number(suggestion._source.id) !== sourcePennameId}
          onSelect={(source: ISuggestionSource) => {
            const id = Number(source.id);

            if (source.type !== 'penname') {
              return;
            }

            setTargetPennameId(id);
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseClick}>CANCEL</Button>
        <Button onClick={handleMergeClick} color="primary" variant="contained" disabled={!targetPennameId || !targetPenname}>
          MERGE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeDialog;
